import { ChevronRight, ExternalLink, ImagePlus, Minus, Plus, Timer, Trash2, Video } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { ExerciseMedia, safeVideoUrl } from '../../components/ExerciseMedia'
import { LineChart } from '../../components/LineChart'
import { useStrata } from '../../data/StrataProvider'
import type { Exercise } from '../../domain/types'
import { displayMass, formatMass, massStep, massUnit, storeMass } from '../../domain/units'

export function ExerciseDetail({ exercise }: { exercise: Exercise }) {
  const { state, updateExercise } = useStrata()
  const [viewedId, setViewedId] = useState(exercise.id)
  const currentExercise = state.exercises.find((item) => item.id === viewedId) ?? exercise
  const [videoUrl, setVideoUrl] = useState(currentExercise.videoUrl ?? '')
  useEffect(() => setVideoUrl(currentExercise.videoUrl ?? ''), [currentExercise.id, currentExercise.videoUrl])
  const variants = useMemo(() => state.exercises
    .filter((item) => item.id !== currentExercise.id && item.category === currentExercise.category)
    .sort((a, b) => Number(b.equipment === currentExercise.equipment) - Number(a.equipment === currentExercise.equipment) || a.name.localeCompare(b.name, 'es'))
    .slice(0, 12), [currentExercise.category, currentExercise.equipment, currentExercise.id, state.exercises])
  const sessions = [...state.sessions].sort((a, b) => a.startedAt.localeCompare(b.startedAt))
  const performances = sessions.flatMap((session) => {
    const sets = session.sets.filter((set) => set.exerciseId === currentExercise.id)
    if (!sets.length) return []
    const weight = Math.max(0, ...sets.map((set) => set.weight ?? 0))
    const reps = Math.max(0, ...sets.map((set) => set.reps ?? 0))
    return [{ label: shortDate(session.startedAt), value: weight ? displayMass(weight, state.preferences.unit) : reps }]
  })
  const lastSession = [...sessions].reverse().find((session) => session.sets.some((set) => set.exerciseId === currentExercise.id))
  const lastSets = lastSession?.sets.filter((set) => set.exerciseId === currentExercise.id).sort((a, b) => a.setNumber - b.setNumber) ?? []

  const adjust = (field: 'increment' | 'restSeconds', amount: number) => updateExercise({
    ...currentExercise,
    [field]: Math.max(field === 'increment' ? .5 : 30, currentExercise[field] + amount),
  })
  const adjustIncrement = (direction: -1 | 1) => updateExercise({
    ...currentExercise,
    increment: Math.max(.1, currentExercise.increment + storeMass(massStep(currentExercise.increment, state.preferences.unit) * direction, state.preferences.unit)),
  })

  const addMedia = async (file?: File) => {
    if (!file) return
    if (file.size > 100 * 1024 * 1024) {
      window.alert('El archivo supera 100 MB. Recórtalo antes de guardarlo para no llenar el almacenamiento del iPhone.')
      return
    }
    await navigator.storage?.persist?.()
    updateExercise({ ...currentExercise, media: file, mediaType: file.type })
  }

  const saveVideo = () => {
    const normalized = videoUrl.trim()
    if (normalized && !safeVideoUrl(normalized)) {
      window.alert('Usa un enlace que empiece con https:// o http://')
      return
    }
    updateExercise({ ...currentExercise, videoUrl: normalized || undefined })
  }

  return (
    <div className="exercise-detail">
      <div className="detail-hero">
        <ExerciseMedia exercise={currentExercise} size={86} />
        <div><h3>{currentExercise.name}</h3><p>{currentExercise.category} · {currentExercise.equipment}</p></div>
      </div>

      <section className="exercise-media-editor">
        {(currentExercise.media || currentExercise.imagePath) && <ExerciseMedia exercise={currentExercise} size={280} controls />}
        <div className="media-actions">
          <label><ImagePlus size={17} />{currentExercise.media ? 'Cambiar foto o video' : 'Añadir foto o video'}<input type="file" accept="image/*,video/*" onChange={(event) => void addMedia(event.target.files?.[0])} /></label>
          {currentExercise.media && <button onClick={() => updateExercise({ ...currentExercise, media: undefined, mediaType: undefined })} aria-label="Eliminar archivo"><Trash2 size={17} /></button>}
        </div>
        <div className="video-url-editor">
          <Video size={16} />
          <input type="url" inputMode="url" placeholder="Pega un enlace de técnica" value={videoUrl} onChange={(event) => setVideoUrl(event.target.value)} onBlur={saveVideo} />
          <button onClick={saveVideo}>Guardar</button>
        </div>
        {safeVideoUrl(currentExercise.videoUrl) && <a className="external-video" href={safeVideoUrl(currentExercise.videoUrl)} target="_blank" rel="noreferrer">Abrir video de referencia <ExternalLink size={14} /></a>}
        <p className="support-copy">El archivo se queda en este dispositivo. El enlace solo se abre cuando tú lo tocas.</p>
      </section>

      {currentExercise.description && <section className="exercise-description"><span className="eyebrow">EJECUCIÓN</span><p>{currentExercise.description}</p></section>}
      {currentExercise.imageAttribution && (
        <p className="image-attribution">Imagen: {currentExercise.imageAttribution.author} · <a href={currentExercise.imageAttribution.licenseUrl} target="_blank" rel="noreferrer">{currentExercise.imageAttribution.license}</a> · <a href={currentExercise.imageAttribution.sourceUrl} target="_blank" rel="noreferrer">wger</a></p>
      )}

      <div className="detail-chips">
        <span><Timer size={14} /> {clock(currentExercise.restSeconds)}</span>
        {currentExercise.metrics.includes('weight') && <span>↗ +{formatMass(currentExercise.increment, state.preferences.unit)}</span>}
      </div>

      <div className="setting-rows">
        {currentExercise.metrics.includes('weight') && (
          <div className="preferred-load-setting"><span><b>Carga inicial personal</b><small>La IA o tú pueden dejarla lista para la próxima sesión.</small></span><input type="number" min="0" max="1000" step={massStep(currentExercise.increment, state.preferences.unit)} value={currentExercise.preferredWeight === undefined ? '' : displayMass(currentExercise.preferredWeight, state.preferences.unit)} placeholder="Auto" onChange={(event) => updateExercise({ ...currentExercise, preferredWeight: event.target.value === '' ? undefined : storeMass(Number(event.target.value), state.preferences.unit) })} /><em>{massUnit(state.preferences.unit)}</em></div>
        )}
        {currentExercise.metrics.includes('weight') && (
          <div className="setting-step"><span>Incremento mínimo</span><button onClick={() => adjustIncrement(-1)}><Minus /></button><strong>{formatMass(currentExercise.increment, state.preferences.unit)}</strong><button onClick={() => adjustIncrement(1)}><Plus /></button></div>
        )}
        <div className="setting-step"><span>Descanso</span><button onClick={() => adjust('restSeconds', -15)}><Minus /></button><strong>{clock(currentExercise.restSeconds)}</strong><button onClick={() => adjust('restSeconds', 15)}><Plus /></button></div>
      </div>

      <LineChart points={performances} suffix={currentExercise.metrics.includes('weight') ? ` ${massUnit(state.preferences.unit)}` : ' reps'} />

      <section className="last-sets">
        <span className="eyebrow">ÚLTIMA VEZ</span>
        {!lastSets.length && <p className="muted">Sin historial</p>}
        {lastSets.map((set) => (
          <div key={set.id}><span>Serie {set.setNumber}</span><strong>{set.weight ? `${formatMass(set.weight, state.preferences.unit)} × ${set.reps}` : `${set.reps ?? 0} reps`}</strong></div>
        ))}
      </section>

      <section className="exercise-variants">
        <div className="variants-heading"><span className="eyebrow">VARIANTES RELACIONADAS</span><small>Mismo grupo · equipo similar primero</small></div>
        <div>
          {variants.map((variant) => (
            <button key={variant.id} onClick={() => setViewedId(variant.id)}>
              <ExerciseMedia exercise={variant} size={48} />
              <span><strong>{variant.name}</strong><small>{variant.equipment}{variant.imagePath ? ' · imagen verificada' : ''}</small></span>
              <ChevronRight size={15} />
            </button>
          ))}
        </div>
        <p className="support-copy">No asignamos fotos aproximadas: si una imagen no fue verificada para ese movimiento, mostramos una ilustración neutra para evitar confundir ejercicios.</p>
      </section>
    </div>
  )
}

const clock = (value: number) => `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`
const shortDate = (value: string) => new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short' }).format(new Date(value))
